import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import AnimatedPage from "../../components/animatedPage/AnimatedPage";
import * as api from "./apiService";
import { CheckIcon, UserIcon } from "@heroicons/react/outline";
import { PencilIcon } from "@heroicons/react/solid";

import { toast } from "react-toastify";

import { useAppSelector } from "../../app/hooks";
import { selectUser } from "../../routes/userProfile/userSlice";

export default function Example() {
  const currentUser = useAppSelector(selectUser);
  const { jobId } = useParams();
  const [activities, setActivities] = useState([]);
  const [jobServiceActivities, setJobServiceActivities] = useState([]);

  useEffect(() => {
    getActivities();
  }, []);

  useEffect(() => {
    getJobServiceActivities();
  }, []);

  const getActivities = async () => {
    const { data } = await api.getJobActivities(jobId);

    data.results.forEach((activity) => {
      activity.timestamp = new Date(activity.timestamp);
      activity.timestamp = activity.timestamp.toLocaleTimeString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    });

    setActivities(data.results);
  };

  const getJobServiceActivities = async () => {
    try {
      const { data } = await api.getJobServiceActivities({ job_id: jobId });
      setJobServiceActivities(data.results);
    } catch (err) {
      toast.error("Error fetching job service activities");
    }
  };

  // (Optional) If you want newest first:
  const serviceRows = useMemo(() => {
    const rows = Array.isArray(jobServiceActivities)
      ? [...jobServiceActivities]
      : [];
    // If timestamp is "12/22/2025" style, sorting reliably can be tricky.
    // If your API can return ISO timestamps, switch to that for perfect sorting.
    return rows;
  }, [jobServiceActivities]);

  return (
    <AnimatedPage>
      <div className="mt-6 max-w-4xl m-auto px-2">
        <div className="flex flex-row">
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-gray-600">
              Job Activity
            </h1>
          </div>
        </div>

        {/* ===== Activity feed ===== */}
        <div className="flow-root mt-6">
          <ul className="-mb-8">
            {activities.map((activity, eventIdx) => (
              <li key={activity.id}>
                <div className="relative pb-8">
                  {eventIdx !== activities.length - 1 ? (
                    <span
                      className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                      aria-hidden="true"
                    />
                  ) : null}

                  <div className="relative flex space-x-3">
                    <div>
                      {activity.activity_type === "S" && (
                        <span
                          className={`
                            ${activity.status === "A" && "bg-sky-500 "}
                            ${activity.status === "S" && "bg-yellow-500 "}
                            ${activity.status === "U" && "bg-indigo-500 "}
                            ${activity.status === "W" && "bg-green-500 "}
                            ${activity.status === "R" && "bg-purple-500 "}
                            ${activity.status === "C" && "bg-blue-500 "}
                            ${activity.status === "T" && "bg-black "}
                            ${activity.status === "I" && "bg-gray-400 "}
                            ${activity.status === "N" && "bg-gray-400 "}
                            ${activity.status === "P" && "bg-red-500 "}
                            ${activity.status === "X" && "bg-gray-500"}
                            h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white`}
                        >
                          <span className="text-white">{activity.status}</span>
                        </span>
                      )}

                      {activity.activity_type !== "S" && (
                        <span className="bg-gray-400 h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white">
                          <span className="text-white">
                            <PencilIcon className="h-4 w-4" />
                          </span>
                        </span>
                      )}
                    </div>

                    <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                      {activity.activity_type === "S" && (
                        <div>
                          <p className="text-sm text-gray-500">
                            {activity.status !== "P" && (
                              <>
                                <span>Status changed to </span>
                                <span className="font-medium text-black">
                                  {activity.status === "A" && "Received"}
                                  {activity.status === "S" && "Assigned"}
                                  {activity.status === "U" && "Submitted"}
                                  {activity.status === "W" &&
                                    "Work In Progress"}
                                  {activity.status === "C" && "Completed"}
                                  {activity.status === "T" && "Canceled"}
                                  {activity.status === "R" && "Review"}
                                  {activity.status === "I" && "Invoiced"}
                                  {activity.status === "N" && "Not Invoiced"}
                                  {activity.status === "X" && "Unassign"}
                                </span>
                              </>
                            )}

                            {activity.status === "P" && (
                              <>
                                <span className="text-sm text-gray-500">
                                  Price changed to
                                </span>
                                <span className="font-medium text-black ml-1">
                                  ${activity.price}
                                </span>
                              </>
                            )}

                            <span className="ml-1">
                              by {activity.user_full_name}
                            </span>
                          </p>
                        </div>
                      )}

                      {activity.activity_type === "P" && (
                        <div>
                          <p className="text-sm text-left text-gray-500">
                            <span className="text-sm text-gray-500">
                              Price changed to
                            </span>
                            <span className="font-medium text-black ml-1">
                              ${activity.price}
                            </span>
                            <span className="ml-1">
                              by {activity.user?.first_name}{" "}
                              {activity.user?.last_name}
                            </span>
                          </p>
                        </div>
                      )}

                      {!currentUser?.isCustomer && (
                        <>
                          {activity.activity_type === "C" && (
                            <div>
                              <p className="text-sm text-left text-gray-500">
                                <span className="font-medium text-black">
                                  Service Added:
                                </span>{" "}
                                {activity.service_name} by{" "}
                                {activity.user_full_name}
                              </p>
                            </div>
                          )}

                          {activity.activity_type === "D" && (
                            <div>
                              <p className="text-sm text-left text-gray-500">
                                <span className="font-medium text-black">
                                  Service Removed:
                                </span>{" "}
                                {activity.service_name} by{" "}
                                {activity.user_full_name}
                              </p>
                            </div>
                          )}

                          {activity.activity_type === "X" && (
                            <div>
                              <p className="text-sm text-left text-gray-500">
                                <span className="font-medium text-black">
                                  Retainer Added:
                                </span>{" "}
                                {activity.service_name} by{" "}
                                {activity.user_full_name}
                              </p>
                            </div>
                          )}

                          {activity.activity_type === "Y" && (
                            <div>
                              <p className="text-sm text-left text-gray-500">
                                <span className="font-medium text-black">
                                  Retainer Removed:
                                </span>{" "}
                                {activity.service_name} by{" "}
                                {activity.user_full_name}
                              </p>
                            </div>
                          )}
                        </>
                      )}

                      {(activity.activity_type === "E" ||
                        activity.activity_type === "A" ||
                        activity.activity_type === "B" ||
                        activity.activity_type === "O" ||
                        activity.activity_type === "F" ||
                        activity.activity_type === "T" ||
                        activity.activity_type === "U" ||
                        activity.activity_type === "R" ||
                        activity.activity_type === "V") && (
                        <div>
                          <p className="text-sm text-left text-gray-500">
                            <span className="font-medium text-black">
                              {activity.activity_type === "E" && "Departure"}
                              {activity.activity_type === "A" && "Arrival"}
                              {activity.activity_type === "B" &&
                                "Complete Before"}
                              {activity.activity_type === "O" && "Airport"}
                              {activity.activity_type === "F" && "FBO"}
                              {activity.activity_type === "T" && "Tail Number"}
                              {activity.activity_type === "U" &&
                                "Photos Uploaded"}
                              {activity.activity_type === "R" && "Job Returned"}
                              {activity.activity_type === "V" && "Accepted"}
                            </span>

                            {activity.activity_type !== "U" &&
                              activity.activity_type !== "V" && (
                                <span> changed </span>
                              )}

                            <span className="ml-1">
                              by {activity.user_full_name}
                            </span>
                          </p>
                        </div>
                      )}

                      {!currentUser?.isCustomer && (
                        <div className="whitespace-nowrap text-right text-sm text-gray-500">
                          {activity.timestamp}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* ===== Job Service Activity Table (added after feed) ===== */}
        <div className="mt-10">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-700">
                Service Activity
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                History of service changes for this job.
              </p>
            </div>
            <div className="text-sm text-gray-500">
              {serviceRows.length ? `${serviceRows.length} records` : ""}
            </div>
          </div>

          {/* Mobile cards (below sm) */}
          <div className="mt-4 space-y-3 sm:hidden">
            {serviceRows.length === 0 ? (
              <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-500">
                No service activity yet.
              </div>
            ) : (
              serviceRows.map((row) => (
                <div
                  key={row.id}
                  className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-800 truncate">
                        {row.service_name}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {row.timestamp}
                      </div>
                    </div>
                    <span className="shrink-0 rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                      #{row.id}
                    </span>
                  </div>

                  <dl className="mt-3 grid grid-cols-1 gap-2 text-sm">
                    <div className="flex justify-between gap-3">
                      <dt className="text-gray-500">Airport</dt>
                      <dd className="font-medium text-gray-800">
                        {row.airport_name || "—"}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-gray-500">FBO</dt>
                      <dd className="font-medium text-gray-800">
                        {row.fbo_name || "—"}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-gray-500">Customer</dt>
                      <dd className="font-medium text-gray-800 text-right">
                        {row.customer_name || "—"}
                      </dd>
                    </div>
                  </dl>
                </div>
              ))
            )}
          </div>

          {/* Desktop table (sm and up) */}
          <div className="mt-4 hidden sm:block">
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Service
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Airport
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        FBO
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Customer
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100 bg-white">
                    {serviceRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-6 text-center text-sm text-gray-500"
                        >
                          No service activity yet.
                        </td>
                      </tr>
                    ) : (
                      serviceRows.map((row) => (
                        <tr key={row.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                            {row.timestamp}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <div className="font-medium">
                              {row.service_name}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                            {row.airport_name || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                            {row.fbo_name || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {row.customer_name || "—"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        {/* ===== end table ===== */}
      </div>
    </AnimatedPage>
  );
}
